import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Horizontal scroll container for tables.
 *
 * The app shell (`protected-sidebar-layout`) sets `overflow-x-hidden` on `<main>`,
 * so a table wide enough to overflow is clipped rather than scrollable unless it
 * carries its own scroll container. Every `<Table>` needs one — either this, or
 * an existing wrapper that already scrolls.
 */
export function TableScrollContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("w-full overflow-x-auto", className)}>{children}</div>
  );
}

/**
 * Minimum table widths by column count, so auto-layout stops starving the
 * wrappable name column on narrow screens. Below these widths the table scrolls
 * horizontally inside its `TableScrollContainer`.
 *
 * Tables with 4 columns or fewer need no minimum — they fit at every width.
 * Keep `md` under ~696px: that is the content area of a 1000px window, and going
 * over it puts a scrollbar on tables that would otherwise fit.
 */
export const TABLE_MIN_WIDTH = {
  /** 5-6 columns */
  md: "min-w-[640px]",
  /** 7-8 columns */
  lg: "min-w-[960px]",
  /** 9+ columns */
  xl: "min-w-[1120px]",
} as const;
